// ── HTTP layer ───────────────────────────────────────────────────────────────────
// One module owns the backend origin, media URL builders, session/admin token
// storage, auth headers and the api() fetch wrapper — every screen shares it.

const BACKEND_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const MEDIA_BASE = BACKEND_URL.replace(/\/api\/v1$/, '');

function getLogoUrl(logo) {
  if (!logo) return '';
  if (logo.startsWith('http')) return logo;
  if (logo.startsWith('/media/')) return MEDIA_BASE + logo;
  return MEDIA_BASE + '/media/' + logo;
}

function getPhotoUrl(photo) {
  if (!photo) return '';
  // If frontend passed an object with a url/file property, try to extract it
  if (typeof photo === 'object') {
    if (photo instanceof File || photo instanceof Blob) return URL.createObjectURL(photo);
    if (photo.url && typeof photo.url === 'string') return photo.url;
    if (photo.attachment && typeof photo.attachment === 'string') return photo.attachment;
    if (photo.name && photo.path && typeof photo.path === 'string') return photo.path;
    // fallback: stringify and attempt to use if it's a string-like object
    try { const s = String(photo); if (s && s !== '[object Object]') return s; } catch (_) {}
    return '';
  }
  // If it's a File/Blob object (freshly uploaded, not yet saved to server), create a temp object URL
  if (photo instanceof File || photo instanceof Blob) return URL.createObjectURL(photo);
  if (typeof photo !== 'string') return '';
  if (photo.startsWith('http')) return photo;
  if (photo.startsWith('/media/')) return MEDIA_BASE + photo;
  return MEDIA_BASE + '/media/' + photo;
}

// ── Session token ────────────────────────────────────────────────────────────────
// The org id on its own is no longer a credential: the server only trusts an
// org id carried inside a signed Bearer token, which the client cannot forge.
// The token is minted by login (org-login / verify-pin) and cleared on logout.
const SESSION_TOKEN_KEY = 'session_token';

function getSessionToken() {
  try { return localStorage.getItem(SESSION_TOKEN_KEY); } catch (_) { return null; }
}

function setSessionToken(token) {
  try {
    if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch (_) { /* private mode: auth simply won't persist */ }
}

// Headers for direct fetch() calls that bypass api().
// Sends the token when we have one; X-Org-Id is still attached as a legacy
// fallback so a rollback to AUTH_REQUIRE_TOKEN=false keeps working unchanged.
function authHeaders(base = {}) {
  const headers = { ...base };
  const token = getSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('org_id');
    if (orgId && !headers['X-Org-Id']) headers['X-Org-Id'] = orgId;
  } else {
    const orgId = localStorage.getItem('org_id');
    if (orgId && !headers['X-Org-Id']) headers['X-Org-Id'] = orgId;
  }
  return headers;
}

// Does this failure mean "your session is no longer usable"?
//
// Deliberately narrow: it must catch an expired/absent/invalid token, but it
// must NOT fire for a role denial, a subscription block, or a validation
// error — re-locking the screen because a form field was wrong would look like
// data loss to the user. Those come back as `{"error": ...}` from our own
// views; DRF's authentication layer speaks in `detail`, so keying on that plus
// the known auth phrasing keeps the two apart.
function isAuthFailure(statusCode, bodyText) {
  if (statusCode === 401) return true;
  if (statusCode !== 403) return false;
  const lower = String(bodyText || '').toLowerCase();
  return (
    lower.includes('session token') ||
    lower.includes('authentication credentials were not provided') ||
    lower.includes('organization is not active') ||
    lower.includes('organization not found') ||
    lower.includes('invalid token') ||
    lower.includes('token not valid') ||
    // A lapsed subscription now cuts an open session too, not just login.
    // Going back to the lock screen is the right move: the PIN check is what
    // raises the expired/renewal modal, and renewal-request stays reachable.
    lower.includes('subscription expired')
  );
}

// The admin panel is a separate privilege level from an organization session,
// so it keeps its own signed token under its own key. An org token is
// structurally rejected by /admin/* (different signing salt) and vice versa.
const ADMIN_TOKEN_KEY = 'admin_token';

function getAdminToken() {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY); } catch (_) { return null; }
}

function setAdminToken(token) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (_) {}
}

// Admin endpoints refuse anything but a real admin token, and a browser
// navigation (clicking a link) cannot attach an Authorization header — so
// every admin call goes through here, including file downloads.
function adminHeaders(base = {}) {
  const headers = { ...base };
  const token = getAdminToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function api(endpoint, method = 'GET', body = null) {
  const orgId = localStorage.getItem('org_id');
  const headers = {};
  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (orgId) headers['X-Org-Id'] = orgId;
  const _token = getSessionToken();
  if (_token) headers['Authorization'] = `Bearer ${_token}`;
  try {
    const memberSession = JSON.parse(localStorage.getItem('member_session') || 'null');
    if (memberSession?.member_id) headers['X-Member-Id'] = memberSession.member_id;
  } catch (_) {}
  const config = { method, headers };
  if (body) {
    if (body instanceof FormData) {
      config.body = body;
    } else {
      config.body = JSON.stringify(body);
    }
  }
  if (!endpoint.startsWith('/')) endpoint = '/' + endpoint;
  try {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, config);
    if (!response.ok) {
      if (response.status === 404) return null;
      let errBody = '';
      try { errBody = await response.text(); } catch (_) {}
      console.error(`API Error ${response.status} on ${method} ${endpoint}:`, errBody);
      // Session tokens are short-lived (12h). When one lapses the server
      // refuses with an auth error — surface that as a clean lock instead of
      // leaving an unlocked screen that can never load data again. Only
      // unambiguous *authentication* failures trigger this; role denials,
      // subscription blocks and validation errors keep their own messages.
      if (isAuthFailure(response.status, errBody)) {
        window.dispatchEvent(new CustomEvent('hisabflow:session-expired'));
      }
      let errMsg = `API error: ${response.status}`;
      try {
        const parsed = JSON.parse(errBody);
        if (parsed.error) errMsg = parsed.error;
        else if (parsed.detail) errMsg = parsed.detail;
        else if (parsed && typeof parsed === 'object') {
          // DRF validation failures look like { field: ['message'] } — show the
          // first real message so a rejected value is explained, not just numbered.
          const first = Object.values(parsed).find(v => Array.isArray(v) && v.length);
          if (first) errMsg = first.join(' ');
        }
      } catch (_) {}
      throw new Error(errMsg);
    }
    if (response.status !== 204) {
      const text = await response.text();
      if (!text) return null;
      try {
        return JSON.parse(text);
      } catch (_) {
        throw new Error(text || 'Invalid server response');
      }
    }
    return null;
  } catch (error) {
    console.error(`API Call Failed: ${method} ${endpoint}`, error);
    throw error;
  }
}

export {
  BACKEND_URL, MEDIA_BASE, getLogoUrl, getPhotoUrl,
  getSessionToken, setSessionToken, authHeaders, isAuthFailure,
  getAdminToken, setAdminToken, adminHeaders, api,
};
